import { useNavigate, useLocation } from "react-router-dom";
import { useActiveOrg } from "@/contexts/ActiveOrgContext";
import { useLicence } from "@/hooks/useLicence";
import { useModuleFavorites } from "@/hooks/useModuleFavorites";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  LayoutDashboard, FolderOpen, FileSpreadsheet, FileText,
  CheckSquare, GitMerge, Archive, Download, Settings,
  BookOpen, Grid3X3, Lock, Star,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";

interface ModuleItem {
  key: string;
  label: string;
  icon: React.ElementType;
  path: string;
  feature: string | null;
  orgTypes?: ("cabinet" | "entreprise")[];
  color: string;
}

const MODULES: ModuleItem[] = [
  { key: "dashboard", label: "Tableau de bord", icon: LayoutDashboard, path: "/dashboard", feature: null, color: "from-violet-500/20 to-violet-500/5 text-violet-600 dark:text-violet-400" },
  { key: "dossiers", label: "Dossiers", icon: FolderOpen, path: "/dossiers", feature: null, orgTypes: ["cabinet"], color: "from-blue-500/20 to-blue-500/5 text-blue-600 dark:text-blue-400" },
  { key: "exercices", label: "Exercices", icon: BookOpen, path: "/dossiers", feature: null, orgTypes: ["entreprise"], color: "from-blue-500/20 to-blue-500/5 text-blue-600 dark:text-blue-400" },
  { key: "etats", label: "États financiers", icon: FileSpreadsheet, path: "/etats-financiers", feature: "montage_social", orgTypes: ["cabinet"], color: "from-emerald-500/20 to-emerald-500/5 text-emerald-600 dark:text-emerald-400" },
  { key: "pointage", label: "Pointage DGI", icon: FileText, path: "/pointage-dgi", feature: "pointage_efi_pdf", orgTypes: ["cabinet"], color: "from-orange-500/20 to-orange-500/5 text-orange-600 dark:text-orange-400" },
  { key: "verification", label: "Vérification", icon: CheckSquare, path: "/verification", feature: null, orgTypes: ["cabinet"], color: "from-violet-500/20 to-violet-500/5 text-violet-600 dark:text-violet-400" },
  { key: "consolidation", label: "Consolidation", icon: GitMerge, path: "/consolidation", feature: "montage_consolide", orgTypes: ["cabinet"], color: "from-pink-500/20 to-pink-500/5 text-pink-600 dark:text-pink-400" },
  { key: "archives", label: "Archives", icon: Archive, path: "/archives", feature: null, orgTypes: ["cabinet"], color: "from-gray-500/20 to-gray-500/5 text-gray-600 dark:text-gray-400" },
  { key: "exports", label: "Exports", icon: Download, path: "/exports", feature: null, orgTypes: ["cabinet"], color: "from-teal-500/20 to-teal-500/5 text-teal-600 dark:text-teal-400" },
  { key: "settings", label: "Paramètres", icon: Settings, path: "/settings", feature: null, color: "from-slate-500/20 to-slate-500/5 text-slate-600 dark:text-slate-400" },
];

export function ModuleMegaMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeOrg } = useActiveOrg();
  const { features } = useLicence();
  const { isFavorite, toggleFavorite } = useModuleFavorites();
  const [open, setOpen] = useState(false);

  const orgType = activeOrg?.organization?.type as "cabinet" | "entreprise" | undefined;

  const visibleModules = MODULES.filter(mod => {
    if (mod.orgTypes && orgType && !mod.orgTypes.includes(orgType)) return false;
    if (mod.key === "info" && orgType === "cabinet") return false;
    if (mod.key === "exercices" && orgType === "cabinet") return false;
    if (mod.key === "dossiers" && orgType === "entreprise") return false;
    return true;
  });

  const isLocked = (mod: ModuleItem) => mod.feature ? features[mod.feature] === false : false;
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + "/");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "gap-2 text-muted-foreground hover:text-foreground",
            open && "bg-muted text-foreground"
          )}
        >
          <Grid3X3 className="w-4 h-4" />
          <span className="hidden sm:inline text-sm">Modules</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[380px] p-3"
        sideOffset={8}
      >
        <div className="grid grid-cols-3 gap-1.5">
          {visibleModules.map(mod => {
            const locked = isLocked(mod);
            const active = isActive(mod.path);
            const fav = isFavorite(mod.key);
            return (
              <div key={mod.key} className="relative group">
                <button
                  disabled={locked}
                  onClick={() => {
                    if (!locked) {
                      navigate(mod.path);
                      setOpen(false);
                    }
                  }}
                  className={cn(
                    "flex flex-col items-center gap-1.5 p-3 rounded-xl text-center transition-all duration-150 w-full",
                    locked
                      ? "opacity-40 cursor-not-allowed"
                      : "hover:bg-muted cursor-pointer",
                    active && "bg-primary/5 ring-1 ring-primary/20"
                  )}
                >
                  <div className={cn(
                    "w-10 h-10 rounded-xl bg-gradient-to-b flex items-center justify-center",
                    mod.color
                  )}>
                    {locked ? (
                      <Lock className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <mod.icon className="w-4.5 h-4.5" />
                    )}
                  </div>
                  <span className={cn(
                    "text-[11px] font-medium leading-tight",
                    active ? "text-primary" : "text-foreground"
                  )}>
                    {mod.label}
                  </span>
                </button>
                {/* Favorite toggle */}
                {!locked && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(mod.key);
                    }}
                    className={cn(
                      "absolute top-1 right-1 p-0.5 rounded-md transition-all",
                      fav
                        ? "text-amber-500 opacity-100"
                        : "text-muted-foreground/40 opacity-0 group-hover:opacity-100 hover:text-amber-500"
                    )}
                    title={fav ? "Retirer des favoris" : "Ajouter aux favoris"}
                  >
                    <Star className={cn("w-3 h-3", fav && "fill-amber-500")} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-2 pt-2 border-t border-border">
          <button
            onClick={() => { navigate("/modules"); setOpen(false); }}
            className="w-full text-xs text-muted-foreground hover:text-foreground text-center py-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            Voir tous les modules →
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
